import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  Layout,
  Typography,
  Space,
  Button,
  Segmented,
  DatePicker,
  Tag,
  Card,
  Modal,
  Form,
  Select,
  Alert,
  Spin,
  Tooltip,
  Badge,
  Divider,
  Progress,
  message,
  List,
} from "antd";
import * as signalR from "@microsoft/signalr";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../context/AuthContext";
import axiosInstance from "../api/axiosInstance";
import ReservatioModal from "./ReservatioModal";
import ChatSidebar from "./ChatSidebar";
import ChatWindow from "./ChatWindow";
import { getChatConnection } from "./chatConnection";

import "./ReservationGantt.ant.css";

const { Header, Content, Sider } = Layout;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

/* ----------------------------- Helpers ----------------------------- */

const isSameDay = (a, b) => {
  const A = new Date(a); A.setHours(0, 0, 0, 0);
  const B = new Date(b); B.setHours(0, 0, 0, 0);
  return A.getTime() === B.getTime();
};

const groupRoomsByCategory = (roomsList) => {
  const grouped = {};
  roomsList.forEach((room) => {
    const cat =
      room.roomType ||
      room.roomTypeName ||
      room.RoomType ||
      room.roomType?.typeName ||
      "Uncategorized";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(room);
  });
  return grouped;
};

const getDifferenceInDays = (start, end) => {
  const d1 = new Date(start);
  const d2 = new Date(end);
  d1.setHours(0, 0, 0, 0);
  d2.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((d2 - d1) / (1000 * 60 * 60 * 24)));
};

const toDayStart = (v) => {
  const d = new Date(v);
  d.setHours(0, 0, 0, 0);
  return d;
};

// half-open [in, out)
const isDateOccupiedByReservation = (res, date) => {
  const day = new Date(date).setHours(0, 0, 0, 0);
  const inD = new Date(res.checkInDate).setHours(0, 0, 0, 0);
  const outD = new Date(res.checkOutDate).setHours(0, 0, 0, 0);
  return inD <= day && day < outD;
};

const norm = (s) =>
  (s ?? "").toString().trim().toLowerCase().replace(/\s+/g, "");

const pick = (obj, keys, fallback = undefined) => {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return fallback;
};

const STATUS_META = {
  confirmed: { label: "Confirmed", className: "status-confirmed" },
  checkedin: { label: "Checked In", className: "status-checkedin" },
  checkedout: { label: "Checked Out", className: "status-checkedout" },
  roomclosure: { label: "Room Closure", className: "status-closure" },
  unallocated: { label: "Unallocated", className: "status-unallocated" },
  cancelled: { label: "Cancelled", className: "status-cancelled" },
  noshow: { label: "No Show", className: "status-noshow" },
};

/**
 * Flatten bookings to "segments" (legs).
 * Supports:
 * - booking.legs
 * - booking.roomBookings / RoomBookings
 * - fallback to booking.roomId + booking.checkInDate/checkOutDate
 */
const flattenToSegments = (bookingsList) => {
  const out = [];
  (bookingsList || []).forEach((b) => {
    const bookingId = pick(b, ["bookingId", "BookingId"]);
    const bookingNo = pick(b, ["bookingNo", "BookingNo"]);
    const status = pick(b, ["status", "Status"]);
    const roomIdFallback = pick(b, ["roomId", "RoomId"]);
    const checkInFallback = pick(b, ["checkInDate", "CheckInDate"]);
    const checkOutFallback = pick(b, ["checkOutDate", "CheckOutDate"]);

    const guestName =
      pick(b, ["guestName", "GuestName"]) ||
      (() => {
        const g = pick(b, ["guest", "Guest"], null);
        const fn = g ? pick(g, ["firstName", "FirstName"], "") : "";
        const ln = g ? pick(g, ["lastName", "LastName"], "") : "";
        const full = `${fn} ${ln}`.trim();
        return full || null;
      })() ||
      null;

    const legsRaw =
      pick(b, ["legs", "Legs"], null) ||
      pick(b, ["roomBookings", "RoomBookings"], null) ||
      pick(b, ["allocations", "Allocations"], null) ||
      [];

    if (Array.isArray(legsRaw) && legsRaw.length > 0) {
      const ordered = [...legsRaw].sort(
        (x, y) =>
          new Date(pick(x, ["checkInDate", "CheckInDate"])) -
          new Date(pick(y, ["checkInDate", "CheckInDate"]))
      );

      const legsCount = ordered.length;

      ordered.forEach((leg, idx) => {
        out.push({
          // identity
          bookingId,
          bookingNo,
          status,
          guestName:
            guestName ||
            pick(leg, ["guestName", "GuestName"]) ||
            pick(leg, ["name", "Name"]) ||
            "Guest",

          // segment meta
          legIndex: idx + 1,
          legsCount,

          // leg allocation
          roomId: pick(leg, ["roomId", "RoomId"], roomIdFallback),
          checkInDate: pick(leg, ["checkInDate", "CheckInDate"], checkInFallback),
          checkOutDate: pick(leg, ["checkOutDate", "CheckOutDate"], checkOutFallback),

          // keep raw for safety/debug
          _rawLeg: leg,
          _rawBooking: b,
        });
      });
      return;
    }

    // fallback: single segment
    out.push({
      bookingId,
      bookingNo,
      status,
      guestName: guestName || "Guest",
      legIndex: 1,
      legsCount: 1,
      roomId: roomIdFallback,
      checkInDate: checkInFallback,
      checkOutDate: checkOutFallback,
      _rawBooking: b,
    });
  });

  // Remove segments that are missing critical dates or roomId
  return out.filter((s) => s.roomId && s.checkInDate && s.checkOutDate);
};

/* ----------------------------- Component ----------------------------- */

const ReservationGanttAnt = () => {
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;

  // chat
  const [chatSessions, setChatSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [chatConnectionStatus, setChatConnectionStatus] = useState("disconnected");
  const [onlineRooms, setOnlineRooms] = useState({});
  const chatHubRef = useRef(null);

  // data
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
   const navigate = useNavigate();
  // timeline
  const [daysToShow, setDaysToShow] = useState(30);
  const [currentStartDate, setCurrentStartDate] = useState(new Date());
  const [dateRange, setDateRange] = useState([]);

  // ui
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [quickView, setQuickView] = useState("all"); // all | arrivals | departures | inhouse
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [showOccupancy, setShowOccupancy] = useState(false);

  // modals
  const [roomClosureModalOpen, setRoomClosureModalOpen] = useState(false);
  const [roomClosureForm] = Form.useForm();
  const [isReservatioModalOpen, setIsReservatioModalOpen] = useState(false);

  // refs for latest data
  const roomsRef = useRef(rooms);
  const bookingsRef = useRef(bookings);
  useEffect(() => { roomsRef.current = rooms; }, [rooms]);
  useEffect(() => { bookingsRef.current = bookings; }, [bookings]);

  /* ----------------------------- Fetch ----------------------------- */

  const fetchRooms = async () => {
    const res = await axiosInstance.get(`/Reservations/rooms-by-hotel/${hotelId}`);
    return res.data;
  };

  const fetchBookings = async () => {
    const res = await axiosInstance.get(`/Reservations/bookings-by-hotel/${hotelId}`);
    return res.data;
  };

  const refreshAll = async () => {
    setLoading(true);
    try {
      const [r, b] = await Promise.all([fetchRooms(), fetchBookings()]);
      setRooms(Array.isArray(r) ? r : []);
      setBookings(Array.isArray(b) ? b : []);
      setError(null);
    } catch (e) {
      console.error(e);
      setError("Failed to load front desk data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hotelId) {
      setLoading(false);
      setError("Hotel ID is missing in auth.");
      return;
    }
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  // recompute date range
  useEffect(() => {
    const s = new Date(currentStartDate);
    s.setHours(0, 0, 0, 0);
    const arr = [];
    for (let i = 0; i < daysToShow; i++) {
      const d = new Date(s);
      d.setDate(s.getDate() + i);
      arr.push(d);
    }
    setDateRange(arr);
  }, [currentStartDate, daysToShow]);

  /* ------------------------ Current affairs (today) ------------------------ */

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const arrivalsToday = useMemo(
    () =>
      (bookings || []).filter(
        (b) => isSameDay(pick(b, ["checkInDate", "CheckInDate"]), today) && norm(pick(b, ["status", "Status"])) !== "cancelled"
      ),
    [bookings, today]
  );

  const departuresToday = useMemo(
    () =>
      (bookings || []).filter(
        (b) => isSameDay(pick(b, ["checkOutDate", "CheckOutDate"]), today) && norm(pick(b, ["status", "Status"])) !== "cancelled"
      ),
    [bookings, today]
  );

  const inHouseToday = useMemo(
    () =>
      (bookings || []).filter((b) => {
        const res = {
          checkInDate: pick(b, ["checkInDate", "CheckInDate"]),
          checkOutDate: pick(b, ["checkOutDate", "CheckOutDate"]),
        };
        return isDateOccupiedByReservation(res, today) && norm(pick(b, ["status", "Status"])) !== "cancelled";
      }),
    [bookings, today]
  );

  // Handy lookup for room number by id
  const roomNoById = useMemo(() => {
    const map = {};
    (rooms || []).forEach((r) => {
      map[r.roomId] = r.roomNumber;
    });
    return map;
  }, [rooms]);

  /* --------------------------- Segments (legs) --------------------------- */

  const ganttSegments = useMemo(() => flattenToSegments(bookings), [bookings]);
  const ganttSegmentsRef = useRef(ganttSegments);
  useEffect(() => { ganttSegmentsRef.current = ganttSegments; }, [ganttSegments]);

  const segmentsByBooking = useMemo(() => {
    const m = new Map();
    (ganttSegments || []).forEach((s) => {
      const id = s.bookingId;
      if (!m.has(id)) m.set(id, []);
      m.get(id).push(s);
    });
    for (const [id, arr] of m.entries()) {
      arr.sort((a, b) => new Date(a.checkInDate) - new Date(b.checkInDate));
      m.set(id, arr);
    }
    return m;
  }, [ganttSegments]);

  /* --------------------------- Booking layout (by segments) --------------------------- */

  const roomReservationsWithLevels = useMemo(() => {
    const map = {};
    const byRoom = new Map();

    (ganttSegments || []).forEach((seg) => {
      if (!byRoom.has(seg.roomId)) byRoom.set(seg.roomId, []);
      byRoom.get(seg.roomId).push({
        ...seg,
        start: new Date(seg.checkInDate),
        end: new Date(seg.checkOutDate),
        level: 0,
        _key: `${seg.bookingId}-${seg.legIndex}-${seg.roomId}-${new Date(seg.checkInDate).toISOString()}`,
      });
    });

    byRoom.forEach((list, roomId) => {
      list.sort((a, b) => a.start - b.start);
      const levels = [];
      list.forEach((res) => {
        let placed = false;
        for (let i = 0; i < levels.length; i++) {
          if (res.start >= levels[i]) {
            res.level = i;
            levels[i] = res.end;
            placed = true;
            break;
          }
        }
        if (!placed) {
          res.level = levels.length;
          levels.push(res.end);
        }
      });
      map[roomId] = list;
    });

    return map;
  }, [ganttSegments]);

  const roomMaxLevels = useMemo(() => {
    const m = {};
    Object.keys(roomReservationsWithLevels).forEach((rid) => {
      const arr = roomReservationsWithLevels[rid] || [];
      const max = arr.reduce((acc, r) => Math.max(acc, r.level), 0);
      m[rid] = max + 1;
    });
    return m;
  }, [roomReservationsWithLevels]);

  const groupedRooms = useMemo(() => groupRoomsByCategory(rooms), [rooms]);

  // Booking-level filters (status + quickView) -> apply to all legs of that booking
  const filteredBookingIds = useMemo(() => {
    const meetsQuick = (b) => {
      const cin = pick(b, ["checkInDate", "CheckInDate"]);
      const cout = pick(b, ["checkOutDate", "CheckOutDate"]);
      const res = { checkInDate: cin, checkOutDate: cout };
      switch (quickView) {
        case "arrivals": return isSameDay(cin, today);
        case "departures": return isSameDay(cout, today);
        case "inhouse": return isDateOccupiedByReservation(res, today);
        default: return true;
      }
    };

    const ids = new Set();
    (bookings || []).forEach((b) => {
      const bStatus = norm(pick(b, ["status", "Status"]));
      if (statusFilter && bStatus !== statusFilter) return;
      if (!meetsQuick(b)) return;
      const id = pick(b, ["bookingId", "BookingId"]);
      if (id !== undefined && id !== null) ids.add(id);
    });
    return ids;
  }, [bookings, statusFilter, quickView, today]);

  const occupancyByDay = useMemo(() => {
    if (!rooms.length || !dateRange.length) return [];
    return dateRange.map((date) => {
      let occupied = 0;
      rooms.forEach((r) => {
        const has = (ganttSegments || []).some(
          (s) => s.roomId === r.roomId && isDateOccupiedByReservation(s, date) && norm(s.status) !== "cancelled"
        );
        if (has) occupied += 1;
      });
      return occupied;
    });
  }, [rooms, ganttSegments, dateRange]);

  /* --------------------------- Room closure flow --------------------------- */

  const fetchAvailableRoomsForClosure = async (startDateISO, endDateISO) => {
    try {
      const res = await axiosInstance.get(`/Reservations/available-rooms`, {
        params: { hotelId, startDate: startDateISO, endDate: endDateISO },
      });
      return Array.isArray(res.data) ? res.data : [];
    } catch {
      return [];
    }
  };

  const reassignBookingRoom = async (bookingId, newRoomId) => {
    await axiosInstance.put(`/Reservations/update/${bookingId}`, {
      roomId: newRoomId,
    });
  };

  const handleCreateClosure = async () => {
    try {
      const vals = await roomClosureForm.validateFields();
      const roomId = vals.roomId;
      const description = vals.description || "";
      const range = vals.range || [];
      const start = range[0]?.startOf("day").toISOString();
      const end = range[1]?.startOf("day").toISOString();

      if (!roomId || !start || !end) {
        message.error("Please select room and date range.");
        return;
      }

      try {
        await axiosInstance.post(`/Reservations/create-room-closure`, {
          roomId,
          startDate: start,
          endDate: end,
          description,
        });
        message.success("Room closure created.");
        setRoomClosureModalOpen(false);
        roomClosureForm.resetFields();
        await refreshAll();
      } catch (err) {
        const data = err?.response?.data || {};
        const conflictsB = data.conflictingBookings || [];
        const conflictsC = data.conflictingClosures || [];

        if (conflictsC.length > 0) {
          Modal.error({
            title: "Conflicting closures",
            content: conflictsC
              .map(
                (c) =>
                  `${new Date(c.startDate).toLocaleDateString()} - ${new Date(c.endDate).toLocaleDateString()}`
              )
              .join(", "),
          });
          return;
        }

        if (conflictsB.length > 0) {
          const available = await fetchAvailableRoomsForClosure(start, end);
          if (available.length === 0) {
            Modal.error({
              title: "No free rooms to reassign",
              content: "Please adjust the closure window or free up rooms.",
            });
            return;
          }

          // naive greedy reassignment
          const pool = [...available];
          for (const b of conflictsB) {
            const alt = pool.find((rm) => rm.roomId !== b.roomId);
            if (!alt) {
              Modal.error({
                title: "Reassignment failed",
                content: `Not enough rooms to move all bookings (stopped at guest ${b.guestName}).`,
              });
              return;
            }
            try {
              await reassignBookingRoom(b.bookingId, alt.roomId);
              const idx = pool.findIndex((x) => x.roomId === alt.roomId);
              if (idx >= 0) pool.splice(idx, 1);
            } catch {
              Modal.error({
                title: "Move failed",
                content: `Could not move booking ${b.bookingId} (${b.guestName}).`,
              });
              return;
            }
          }

          // try closure again
          await axiosInstance.post(`/Reservations/create-room-closure`, {
            roomId,
            startDate: start,
            endDate: end,
            description,
          });
          message.success("Room closure created (after reassignments).");
          setRoomClosureModalOpen(false);
          roomClosureForm.resetFields();
          await refreshAll();
        } else {
          Modal.error({
            title: "Failed to create closure",
            content: data.message || "Unknown error.",
          });
        }
      }
    } catch {
      /* validation error shown inline */
    }
  };

  /* ----------------------------- Chat (receive + poll) ----------------------------- */

  const normalizeSender = (v) =>
    typeof v === "string" ? v : v?.name ?? v?.role ?? v?.username ?? "staff";
  const seenMsgKeysRef = useRef(new Set());
  const joinedRoomsRef = useRef(new Set());
  const handlersAttachedRef = useRef(false);

  // guards for send & polling
  const sendInFlightRef = useRef(false);
  const lastSendKeyRef = useRef({ key: null, at: 0 });
  const chatPollInFlightRef = useRef(false);

  const getGuestNameForRoom = (roomNumber, fallback) => {
    const room = roomsRef.current.find((r) => String(r.roomNumber) === String(roomNumber));
    if (!room) return fallback || "Guest";

    const today0 = new Date(); today0.setHours(0, 0, 0, 0);

    // Use legs-aware segments (so mid-stay moves are reflected)
    const hit = (ganttSegmentsRef.current || [])
      .filter((s) => s.roomId === room.roomId)
      .find((s) => isDateOccupiedByReservation(s, today0) && norm(s.status) !== "cancelled");

    return hit?.guestName || fallback || "Guest";
  };

  const fetchChatMessages = async () => {
    if (chatPollInFlightRef.current) return;
    chatPollInFlightRef.current = true;
    try {
      const res = await axiosInstance.get("/Chat");
      const msgs = Array.isArray(res.data) ? res.data : [];

      const map = new Map();
      msgs.forEach((m) => {
        const roomNumber = m.room ?? m.roomNumber ?? null;
        const sid = `room-${roomNumber || "unknown"}`;
        if (!map.has(sid)) {
          map.set(sid, {
            sessionId: sid,
            roomNumber,
            guestName: getGuestNameForRoom(roomNumber, m.guestName),
            messages: [],
          });
        }
        map.get(sid).messages.push({
          text: m.text,
          sender: typeof m.sender === "string" ? m.sender : m.sender?.name ?? m.sender?.role ?? "guest",
          timestamp: m.timestamp,
          roomNumber,
        });
      });

      const sessions = Array.from(map.values()).sort((a, b) => {
        const at = a.messages[a.messages.length - 1]?.timestamp || 0;
        const bt = b.messages[b.messages.length - 1]?.timestamp || 0;
        return new Date(bt) - new Date(at);
      });

      setChatSessions(sessions);
      if (!activeSessionId && sessions.length) setActiveSessionId(sessions[0].sessionId);
    } catch (e) {
      console.error("GET /Chat failed", e);
    } finally {
      chatPollInFlightRef.current = false;
    }
  };

  useEffect(() => {
    fetchChatMessages();
    const t = setInterval(fetchChatMessages, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hotelId) return;
    const conn = getChatConnection(axiosInstance.defaults.baseURL || "");
    chatHubRef.current = conn;

    const setStatus = (s) => setChatConnectionStatus(s);

    const onAnyChatMessage = (...args) => {
      const msg =
        args.length === 1 && typeof args[0] === "object"
          ? args[0]
          : { sender: args[0], text: args[1], timestamp: args[2], room: args[3] };

      const sender = normalizeSender(msg.sender);
      const text = msg.text;
      const timestamp = msg.timestamp;
      const room = msg.room;

      const sid = `room-${room || "unknown"}`;
      const key = `${sid}|${timestamp}|${sender}|${text}`;
      if (seenMsgKeysRef.current.has(key)) return;
      seenMsgKeysRef.current.add(key);

      setChatSessions((prev) => {
        const arr = Array.isArray(prev) ? prev : [];
        const existing = arr.find((s) => s.sessionId === sid);
        const guest = existing?.guestName || getGuestNameForRoom(room, null);
        const msgObj = {
          text,
          sender,
          timestamp,
          roomNumber: room || existing?.roomNumber || null,
        };
        if (!existing) {
          return [
            {
              sessionId: sid,
              roomNumber: room || null,
              guestName: guest,
              messages: [msgObj],
            },
            ...arr,
          ];
        }
        return arr.map((s) =>
          s.sessionId === sid
            ? {
                ...s,
                roomNumber: room || s.roomNumber,
                guestName: guest || s.guestName,
                messages: [...(s.messages || []), msgObj],
              }
            : s
        );
      });
      setActiveSessionId((cur) => cur || sid);
    };

    (async () => {
      try {
        if (conn.state === signalR.HubConnectionState.Disconnected) {
          setStatus("connecting");
          await conn.start();
          setStatus("connected");
        } else {
          setStatus("connected");
        }

        if (!handlersAttachedRef.current) {
          conn.off("ReceiveMessage");
          conn.on("ReceiveMessage", onAnyChatMessage);

          conn.off("BookingsChanged");
          conn.on("BookingsChanged", async () => {
            try { const b = await fetchBookings(); setBookings(Array.isArray(b) ? b : []); } catch {}
          });

          conn.off("PresenceSnapshot");
          conn.on("PresenceSnapshot", (snapshot) => setOnlineRooms(snapshot || {}));

          conn.off("PresenceChanged");
          conn.on("PresenceChanged", (snapshot) => setOnlineRooms(snapshot || {}));

          conn.off("ReservationUpdated");
          conn.on("ReservationUpdated", async () => {
            try { const b = await fetchBookings(); setBookings(Array.isArray(b) ? b : []); } catch {}
          });

          handlersAttachedRef.current = true;
        }

        try { await conn.invoke("JoinStaff"); } catch {}
      } catch (e) {
        console.error(e);
        setStatus("disconnected");
      }
    })();

    conn.onclose(() => setChatConnectionStatus("disconnected"));
    conn.onreconnecting(() => setChatConnectionStatus("reconnecting"));
    conn.onreconnected(() => setChatConnectionStatus("connected"));

    return () => {
      try {
        conn.off("ReceiveMessage", onAnyChatMessage);
        conn.off("BookingsChanged");
        conn.off("PresenceSnapshot");
        conn.off("PresenceChanged");
        conn.off("ReservationUpdated");
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  // join each room we see in sessions (best-effort)
  useEffect(() => {
    const conn = chatHubRef.current;
    if (!conn || conn.state !== signalR.HubConnectionState.Connected) return;
    const roomsToJoin = new Set(
      (chatSessions || [])
        .map((s) => (s?.roomNumber != null ? String(s.roomNumber) : ""))
        .filter(Boolean)
    );
    roomsToJoin.forEach(async (rn) => {
      if (!joinedRoomsRef.current.has(rn)) {
        try {
          await conn.invoke("JoinRoom", rn);
          joinedRoomsRef.current.add(rn);
        } catch {}
      }
    });
  }, [chatSessions]);

  /* ----------------------------- UI derived ----------------------------- */

  const DAY_WIDTH = 84;
  const totalRooms = rooms.length;
  const todayIdx = dateRange.findIndex((d) => d.getTime() === today.getTime());
  const todayOcc = todayIdx >= 0 ? occupancyByDay[todayIdx] : 0;

  const onDaysChange = (val) => setDaysToShow(Number(val));
  const setToday = () => setCurrentStartDate(new Date());
  const prevDay = () => setCurrentStartDate((d) => dayjs(d).add(-1, "day").toDate());
  const nextDay = () => setCurrentStartDate((d) => dayjs(d).add(1, "day").toDate());
  const onPickRange = (val) => {
    if (!val || val.length !== 2) return;
    const s = val[0].startOf("day").toDate();
    const e = val[1].startOf("day").toDate();
    setCurrentStartDate(s);
    const diff = getDifferenceInDays(s, e) + 1;
    if (diff > 0) setDaysToShow(diff);
  };

  const currentDateLabel = dateRange.length
    ? dateRange[0].toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" })
    : "";
  const rangeEndLabel = dateRange.length
    ? dateRange[dateRange.length - 1].toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" })
    : "";

  /* ----------------------------- Render ----------------------------- */

  if (loading) {
    return (
      <div className="ant-gantt-center">
        <Spin tip="Loading front desk..." size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="ant-gantt-center" style={{ maxWidth: 560 }}>
        <Alert type="error" message="Unable to load" description={error} showIcon />
      </div>
    );
  }

  if (rooms.length === 0) {
    return (
      <div className="ant-gantt-center">
        <Alert type="info" message="No rooms available for this hotel." />
      </div>
    );
  }

  return (
    <Layout className="ant-gantt-layout">
      <Header className="ant-gantt-headerbar">
        <div className="ant-gantt-headerbar-left">
          <Space size="large" align="center">
            <Title level={4} style={{ margin: 0 }}>Front Desk</Title>
            <Space>
              <Tag bordered={false} color="blue">
                {currentDateLabel} → {rangeEndLabel}
              </Tag>
              <Tooltip title="Today’s occupancy">
                <Space>
                  <Progress
                    type="circle"
                    size={36}
                    percent={Math.round((todayOcc / Math.max(totalRooms, 1)) * 100)}
                  />
                  <Text type="secondary">{todayOcc}/{totalRooms}</Text>
                </Space>
              </Tooltip>
            </Space>
          </Space>
        </div>

        <div className="ant-gantt-headerbar-right">
          <Space wrap>
            <Button onClick={() => setShowOccupancy((s) => !s)}>
              {showOccupancy ? "Hide" : "Show"} Occupancy
            </Button>
            <Button onClick={() => setRoomClosureModalOpen(true)}>Room Closure</Button>
            <Button
              type="primary"
              onClick={() => navigate("/hotels/bookingpage")}
            >
              + Reservation
            </Button>
            <Badge
              status={
                chatConnectionStatus === "connected"
                  ? "success"
                  : chatConnectionStatus === "connecting"
                  ? "processing"
                  : "error"
              }
              text={`Chat: ${chatConnectionStatus}`}
            />
          </Space>
        </div>
      </Header>

      <Layout>
        <Content className="ant-gantt-content">
          {/* Toolbar */}
          <Card className="ant-gantt-toolbar" size="small">
            <Space wrap align="center">
              <Segmented
                options={[
                  { label: "7d", value: 7 },
                  { label: "14d", value: 14 },
                  { label: "30d", value: 30 },
                  { label: "60d", value: 60 },
                ]}
                value={daysToShow}
                onChange={onDaysChange}
              />

              <Space.Compact>
                <Button onClick={setToday}>Today</Button>
                <Button onClick={prevDay}>Prev</Button>
                <Button onClick={nextDay}>Next</Button>
              </Space.Compact>

              <RangePicker
                onChange={onPickRange}
                allowClear
                value={[
                  dayjs(currentStartDate),
                  dayjs(currentStartDate).add(daysToShow - 1, "day"),
                ]}
              />

              <Divider type="vertical" />

              {/* Quick views */}
              <Space.Compact>
                <Segmented
                  value={quickView}
                  onChange={setQuickView}
                  options={[
                    { label: `All`, value: "all" },
                    { label: `Arrivals (${arrivalsToday.length})`, value: "arrivals" },
                    { label: `Departures (${departuresToday.length})`, value: "departures" },
                    { label: `In-House (${inHouseToday.length})`, value: "inhouse" },
                  ]}
                />
              </Space.Compact>

              <Divider type="vertical" />

              <Space size={[8, 8]} wrap>
                {Object.entries(STATUS_META).map(([key, meta]) => (
                  <Tag.CheckableTag
                    key={key}
                    checked={statusFilter === key}
                    onChange={() => setStatusFilter(statusFilter === key ? null : key)}
                    className={`legend-tag ${meta.className} ${statusFilter === key ? "active" : ""}`}
                  >
                    {meta.label}
                  </Tag.CheckableTag>
                ))}
                {statusFilter && (
                  <Button size="small" onClick={() => setStatusFilter(null)}>Reset Status</Button>
                )}
              </Space>
            </Space>
          </Card>

          {/* Sticky date header */}
          <div className="ant-gantt-header sticky">
            <div className="ant-gantt-header-room">Room</div>
            <div className="ant-gantt-header-days">
              {dateRange.map((date) => {
                const isWeekend = [0, 6].includes(date.getDay());
                const isTodayCol = isSameDay(date, today);
                const cls = ["ant-gantt-header-day", isWeekend ? "weekend" : "", isTodayCol ? "today" : ""]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <div key={date.toISOString()} className={cls} style={{ width: DAY_WIDTH }}>
                    {date.toLocaleDateString("en-US", { weekday: "short", day: "2-digit", month: "short" })}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Body */}
          <div className="ant-gantt-body">
            {Object.entries(groupedRooms).map(([catName, list]) => (
              <Card
                key={catName}
                size="small"
                className="ant-gantt-cat"
                title={<strong>{catName}</strong>}
              >
                {list.map((room) => {
                  const roomBookings = roomReservationsWithLevels[room.roomId] || [];
                  const maxLevels = roomMaxLevels[room.roomId] || 1;

                  return (
                    <div
                      key={room.roomId}
                      className="ant-gantt-row"
                      style={{ position: "relative", height: `${maxLevels * 44}px` }}
                    >
                      <div className="ant-gantt-row-label">
                        <Space>
                          <Text>Room {room.roomNumber}</Text>
                          {onlineRooms?.[String(room.roomNumber)] > 0 && (
                            <Badge status="processing" text="online" />
                          )}
                        </Space>
                      </div>

                      <div className="ant-gantt-row-slots">
                        {dateRange.map((date) => {
                          const isWeekend = [0, 6].includes(date.getDay());
                          const isTodayCol = isSameDay(date, today);
                          const cls = ["ant-gantt-day-cell", isWeekend ? "weekend" : "", isTodayCol ? "today" : ""]
                            .filter(Boolean)
                            .join(" ");
                          return <div key={date.toISOString()} className={cls} style={{ width: DAY_WIDTH }} />;
                        })}

                        {roomBookings.map((res) => {
                          // apply booking-level filter to all legs
                          if (statusFilter || quickView !== "all") {
                            if (!filteredBookingIds.has(res.bookingId)) return null;
                          }

                          if (!dateRange.length) return null;

                          // IMPORTANT: half-open rendering [in, out)
                          const chartStart = toDayStart(dateRange[0]);
                          const chartEndExclusive = toDayStart(
                            dayjs(dateRange[dateRange.length - 1]).add(1, "day").toDate()
                          );

                          const startDay = toDayStart(res.checkInDate);
                          const endDay = toDayStart(res.checkOutDate);

                          if (endDay <= chartStart || startDay >= chartEndExclusive) return null;

                          const effStart = startDay < chartStart ? chartStart : startDay;
                          const effEnd = endDay > chartEndExclusive ? chartEndExclusive : endDay;

                          const startOffset = getDifferenceInDays(chartStart, effStart);
                          const endOffset = getDifferenceInDays(chartStart, effEnd);
                          const lengthDays = Math.max(1, endOffset - startOffset);

                          const leftPx = startOffset * DAY_WIDTH;
                          const widthPx = lengthDays * DAY_WIDTH;
                          const topPx = res.level * (34 + 6);

                          const sKey = norm(res.status);
                          const css = STATUS_META[sKey]?.className || "status-confirmed";

                          const legs = segmentsByBooking.get(res.bookingId) || [];
                          const route = legs.length
                            ? legs
                                .map((x) => `Room ${roomNoById[x.roomId] ?? "?"}`)
                                .join(" → ")
                            : `Room ${room.roomNumber}`;

                          return (
                            <Tooltip
                              key={res._key || res.bookingId}
                              title={
                                <div style={{ maxWidth: 280 }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                                    <strong style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {res.guestName}
                                    </strong>
                                    {res.legsCount > 1 && (
                                      <span style={{ fontSize: 12, opacity: 0.9 }}>
                                        {res.legIndex}/{res.legsCount}
                                      </span>
                                    )}
                                  </div>
                                  <div>Status: {STATUS_META[sKey]?.label || res.status}</div>
                                  <div>
                                    Segment:{" "}
                                    {new Date(res.checkInDate).toLocaleDateString()} →{" "}
                                    {new Date(res.checkOutDate).toLocaleDateString()}
                                  </div>
                                  <div>Room {room.roomNumber}</div>
                                  {res.legsCount > 1 && (
                                    <div style={{ marginTop: 6, opacity: 0.9 }}>
                                      Move path: {route}
                                    </div>
                                  )}
                                </div>
                              }
                            >
                              <div
                                className={`ant-gantt-reservation ${css}`}
                                style={{ left: leftPx, width: widthPx, top: topPx, height: 34 }}
                                role="button"
                              >
                                <span className="ant-gantt-reservation-text" style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {res.guestName}
                                  </span>
                                  {res.legsCount > 1 && (
                                    <span style={{ fontSize: 12, opacity: 0.9 }}>
                                      {res.legIndex}/{res.legsCount}
                                    </span>
                                  )}
                                </span>
                              </div>
                            </Tooltip>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </Card>
            ))}
          </div>

          {/* Occupancy strip */}
          {showOccupancy && dateRange.length > 0 && (
            <Card className="ant-gantt-occupancy" size="small">
              <div className="ant-gantt-occupancy-label">Occupied / {rooms.length}</div>
              <div className="ant-gantt-occupancy-days">
                {occupancyByDay.map((count, idx) => (
                  <div key={idx} className="ant-gantt-occupancy-cell" style={{ width: DAY_WIDTH }}>
                    {count}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </Content>

        {/* Right chat / guests panel */}
        <Sider
          className="ant-gantt-sider"
          width={360}
          collapsible
          collapsed={chatCollapsed}
          onCollapse={setChatCollapsed}
          theme="light"
        >
          <div style={{ padding: 12 }}>
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Card size="small" title="Guests">
                {chatSessions.length > 0 ? (
                  <ChatSidebar
                    sessions={chatSessions}
                    activeSessionId={activeSessionId}
                    onSelectSession={setActiveSessionId}
                    onlineRooms={onlineRooms}
                  />
                ) : (
                  <>
                    <List
                      header={<strong>Arrivals Today ({arrivalsToday.length})</strong>}
                      dataSource={arrivalsToday}
                      locale={{ emptyText: "No arrivals today" }}
                      renderItem={(b) => (
                        <List.Item>
                          <Space direction="vertical" size={0}>
                            <Text strong>{pick(b, ["guestName", "GuestName"], "Guest")}</Text>
                            <Text type="secondary">
                              Room {roomNoById[pick(b, ["roomId", "RoomId"])] || "?"} • Check-in{" "}
                              {new Date(pick(b, ["checkInDate", "CheckInDate"])).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </Text>
                          </Space>
                        </List.Item>
                      )}
                    />
                    <Divider style={{ margin: "12px 0" }} />
                    <List
                      header={<strong>Departures Today ({departuresToday.length})</strong>}
                      dataSource={departuresToday}
                      locale={{ emptyText: "No departures today" }}
                      renderItem={(b) => (
                        <List.Item>
                          <Space direction="vertical" size={0}>
                            <Text strong>{pick(b, ["guestName", "GuestName"], "Guest")}</Text>
                            <Text type="secondary">
                              Room {roomNoById[pick(b, ["roomId", "RoomId"])] || "?"} • Check-out{" "}
                              {new Date(pick(b, ["checkOutDate", "CheckOutDate"])).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </Text>
                          </Space>
                        </List.Item>
                      )}
                    />
                    <Divider style={{ margin: "12px 0" }} />
                    <List
                      header={<strong>In-House ({inHouseToday.length})</strong>}
                      dataSource={inHouseToday}
                      locale={{ emptyText: "No in-house guests" }}
                      renderItem={(b) => (
                        <List.Item>
                          <Space direction="vertical" size={0}>
                            <Text strong>{pick(b, ["guestName", "GuestName"], "Guest")}</Text>
                            <Text type="secondary">Room {roomNoById[pick(b, ["roomId", "RoomId"])] || "?"}</Text>
                          </Space>
                        </List.Item>
                      )}
                    />
                  </>
                )}
              </Card>

              <Card size="small" title="Conversation">
                {activeSessionId ? (
                  <ChatWindow
                    session={chatSessions.find((s) => s.sessionId === activeSessionId)}
                    chatMessages={chatSessions.find((s) => s.sessionId === activeSessionId)?.messages || []}
                    onSendMessage={async (m) => {
                      const session = chatSessions.find((s) => s.sessionId === activeSessionId);
                      const text = (m?.text || "").trim();
                      const sender = (m?.sender || "staff").toString();
                      const room = String(session?.roomNumber || "");
                      if (!text || !room) return;

                      // anti-double-tap (2s) + single in-flight guard
                      const key = `${room}|${text}`;
                      const now = Date.now();
                      if (lastSendKeyRef.current.key === key && now - lastSendKeyRef.current.at < 2000) return;
                      if (sendInFlightRef.current) return;
                      sendInFlightRef.current = true;
                      lastSendKeyRef.current = { key, at: now };

                      const tempId = `${now}_${Math.random().toString(36).slice(2)}`;
                      const optimistic = {
                        text,
                        sender,
                        tempId,
                        timestamp: new Date().toISOString(),
                        roomNumber: room,
                      };

                      setChatSessions((prev) =>
                        prev.map((s) =>
                          s.sessionId === activeSessionId
                            ? { ...s, messages: [...(s.messages || []), optimistic] }
                            : s
                        )
                      );

                      try {
                        await axiosInstance.post("/Chat", { text, sender, room });

                        // clear tempId for delivered message
                        setChatSessions((prev) =>
                          prev.map((s) =>
                            s.sessionId === activeSessionId
                              ? {
                                  ...s,
                                  messages: (s.messages || []).map((x) =>
                                    x.tempId === tempId ? { ...x, tempId: undefined } : x
                                  ),
                                }
                              : s
                          )
                        );

                        fetchChatMessages();
                        setTimeout(async () => {
                          try {
                            const b = await fetchBookings();
                            setBookings(Array.isArray(b) ? b : []);
                          } catch {}
                        }, 200);
                      } catch {
                        message.error("Failed to send message");
                        setChatSessions((prev) =>
                          prev.map((s) =>
                            s.sessionId === activeSessionId
                              ? { ...s, messages: (s.messages || []).filter((x) => x.tempId !== tempId) }
                              : s
                          )
                        );
                      } finally {
                        sendInFlightRef.current = false;
                      }
                    }}
                  />
                ) : (
                  <Alert type="info" message="Select a guest chat on the left." />
                )}
              </Card>
            </Space>
          </div>
        </Sider>
      </Layout>

      {/* Room Closure Modal */}
      <Modal
        open={roomClosureModalOpen}
        title="Create Room Closure"
        onCancel={() => setRoomClosureModalOpen(false)}
        onOk={handleCreateClosure}
        okText="Create"
      >
        <Form form={roomClosureForm} layout="vertical">
          <Form.Item label="Room" name="roomId" rules={[{ required: true, message: "Select a room" }]}>
            <Select
              showSearch
              placeholder="Select room"
              optionFilterProp="label"
              options={(rooms || []).map((r) => ({
                value: r.roomId,
                label: `Room ${r.roomNumber} (${r.roomType || r.RoomType || "Type"})`,
              }))}
            />
          </Form.Item>
          <Form.Item label="Dates" name="range" rules={[{ required: true, message: "Select date range" }]}>
            <RangePicker />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Select
              placeholder="Select reason"
              allowClear
              options={[
                { value: "Deep cleaning", label: "Deep cleaning" },
                { value: "Maintenance", label: "Maintenance" },
                { value: "Renovation", label: "Renovation" },
                { value: "Other", label: "Other" },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Create Reservation Modal */}
      <ReservatioModal
        open={isReservatioModalOpen}
        onClose={() => setIsReservatioModalOpen(false)}
        onReservationSuccess={refreshAll}
      />
    </Layout>
  );
};

export default ReservationGanttAnt;
